import { api } from '../api';
import type { ApiResponse } from '../api';
import { ApiContractError, normalizeListResult, normalizeNamedRecord, optionalArray, requireNumber, requireRecord, normalizeEntity } from '../api/contracts';
import type { ListResult } from '../api/contracts';

export interface ProductListItem {
  id: string; name: string; description?: string; barcode?: string; sku?: string;
  categoryId: string; category?: Pick<Category, 'id' | 'name'>;
  basePrice: number; costPrice?: number; reorderPoint?: number; imageUrl?: string;
  taxId?: string; variantCount: number; trackInventory: boolean; isActive: boolean;
  createdAt: string; updatedAt: string;
}
// Related resources have separate endpoints and are absent from the detail DTO.
export type ProductDetails = ProductListItem;
export type Product = ProductListItem;
export interface ProductImage { id: string; url?: string; originalName?: string }
export interface ProductVariant {
  id: string; productId: string; name: string; sku?: string; barcode?: string;
  price?: number; costPrice?: number; isActive: boolean;
  product?: Pick<ProductListItem, 'id' | 'name' | 'sku' | 'basePrice'>;
}
export interface Category {
  id: string; name: string; description?: string; parentId?: string;
  children?: Category[]; isActive: boolean; sortOrder?: number; imageUrl?: string;
}
export interface CreateProductDto {
  name: string; categoryId: string; basePrice: number; description?: string;
  barcode?: string; sku?: string; costPrice?: number; reorderPoint?: number;
  trackInventory?: boolean; imageUrl?: string; taxId?: string; isActive?: boolean;
}
export type UpdateProductDto = Partial<CreateProductDto>;
export interface ProductFilters {
  page?: number; limit?: number; search?: string; categoryId?: string;
  isActive?: boolean; minPrice?: number; maxPrice?: number;
  sortBy?: 'name' | 'basePrice' | 'createdAt'; sortOrder?: 'asc' | 'desc';
}
export type ProductsResponse = ListResult<ProductListItem>;
export interface CreateCategoryDto { name: string; description?: string; parentId?: string; isActive?: boolean; imageUrl?: string }
export type UpdateCategoryDto = Partial<CreateCategoryDto>;
export type CreateProductVariantDto = Omit<ProductVariant, 'id'>;

export function normalizeProduct(value: unknown, context: string): ProductListItem {
  const product = { ...normalizeEntity<ProductListItem>(value, context, ['variantCount']) };
  if (typeof product.trackInventory !== 'boolean') throw new ApiContractError(`${context}.trackInventory`, 'boolean');
  product.basePrice = requireNumber(product.basePrice, `${context}.basePrice`);
  for (const key of ['costPrice', 'reorderPoint'] as const) if (product[key] != null) product[key] = requireNumber(product[key], `${context}.${key}`);
  return product;
}
function normalizeCategory(value: unknown, context: string): Category {
  const category = normalizeNamedRecord<Category>(value, context);
  return { ...category, children: optionalArray<unknown>(category.children, `${context}.children`).map(item => normalizeCategory(item, `${context}.child`)) };
}
export function productPayload(data: CreateProductDto | UpdateProductDto): UpdateProductDto {
  const { name, description, barcode, sku, categoryId, basePrice, costPrice, reorderPoint, trackInventory, imageUrl, taxId, isActive } = data;
  return Object.fromEntries(Object.entries({ name, description, barcode, sku, categoryId, basePrice, costPrice, reorderPoint, trackInventory, imageUrl, taxId, isActive }).filter(([, value]) => value !== undefined));
}
export const productsApi = {
  async getProducts(filters: ProductFilters = {}): Promise<ProductsResponse> {
    const response = await api.get<ApiResponse<unknown>>('/products', { params: { search: filters.search, categoryId: filters.categoryId } });
    const result = normalizeListResult(response.data, 'products', normalizeProduct);
    result.items = result.items.filter(item => (filters.isActive === undefined || item.isActive === filters.isActive) && (filters.minPrice === undefined || item.basePrice >= filters.minPrice) && (filters.maxPrice === undefined || item.basePrice <= filters.maxPrice));
    result.total = result.items.length;
    return result;
  },
  async getProduct(id: string): Promise<ProductDetails> {
    const response = await api.get<ApiResponse<unknown>>(`/products/${id}`);
    return normalizeProduct(response.data.data, 'product');
  },
  async createProduct(data: CreateProductDto): Promise<ProductDetails> {
    const response = await api.post<ApiResponse<unknown>>('/products', productPayload(data));
    return normalizeProduct(response.data.data, 'product.create');
  },
  async updateProduct(id: string, data: UpdateProductDto): Promise<ProductDetails> {
    const response = await api.patch<ApiResponse<unknown>>(`/products/${id}`, productPayload(data));
    return normalizeProduct(response.data.data, 'product.update');
  },
  async deleteProduct(id: string): Promise<void> { await api.delete(`/products/${id}`); },
  async bulkDeleteProducts(ids: string[]): Promise<{ deletedCount: number }> {
    let deletedCount = 0;
    for (const id of ids) { await productsApi.deleteProduct(id); deletedCount++; }
    return { deletedCount };
  },
  async getProductImages(productId: string): Promise<ProductImage[]> {
    const response = await api.get<ApiResponse<unknown>>(`/products/${productId}/images`);
    return optionalArray<ProductImage>(response.data.data, 'product.images');
  },
  async uploadProductImage(productId: string, file: File, isPrimary = false): Promise<ProductImage> {
    const formData = new FormData(); formData.append('file', file);
    const response = await api.post<ApiResponse<ProductImage>>(`/products/${productId}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    const image = requireRecord(response.data.data, 'product.image') as unknown as ProductImage;
    if (isPrimary && image.url) await productsApi.updateProduct(productId, { imageUrl: image.url });
    return image;
  },
  async deleteProductImage(productId: string, imageId: string): Promise<void> { await api.delete(`/products/${productId}/images/${imageId}`); },
  async getProductVariants(productId: string): Promise<ProductVariant[]> {
    const response = await api.get<ApiResponse<unknown>>('/product-variants', { params: { productId } });
    return normalizeListResult<ProductVariant>(response.data, 'product.variants', normalizeNamedRecord).items;
  },
  async addProductVariant(data: CreateProductVariantDto): Promise<ProductVariant> {
    const response = await api.post<ApiResponse<ProductVariant>>('/product-variants', data);
    return normalizeNamedRecord(response.data.data, 'variant.create');
  },
  async updateProductVariant(id: string, data: Partial<Omit<ProductVariant, 'id' | 'productId'>>): Promise<ProductVariant> {
    const response = await api.patch<ApiResponse<ProductVariant>>(`/product-variants/${id}`, data);
    return normalizeNamedRecord(response.data.data, 'variant.update');
  },
  async deleteProductVariant(id: string): Promise<void> { await api.delete(`/product-variants/${id}`); },
  async getCategories(): Promise<Category[]> {
    const response = await api.get<ApiResponse<unknown>>('/categories');
    return normalizeListResult(response.data, 'categories', normalizeCategory).items;
  },
  async createCategory(data: CreateCategoryDto): Promise<Category> {
    const response = await api.post<ApiResponse<Category>>('/categories', data);
    return normalizeCategory(response.data.data, 'category.create');
  },
  async updateCategory(id: string, data: UpdateCategoryDto): Promise<Category> {
    const response = await api.patch<ApiResponse<Category>>(`/categories/${id}`, data);
    return normalizeCategory(response.data.data, 'category.update');
  },
  async deleteCategory(id: string): Promise<void> { await api.delete(`/categories/${id}`); },
  async getSearchSuggestions(query: string): Promise<string[]> { return (await productsApi.getProducts({ search: query })).items.slice(0, 10).map(item => item.name); },
};
export default productsApi;
